import { useMutation } from '@apollo/client/react';
import { PipelineStatusBadge } from '@optools/components/pipelines/pipeline-status-badge';
import { TeamSpot } from '@optools/components/team-spot';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@optools/components/ui/alert-dialog';
import { Badge } from '@optools/components/ui/badge';
import { Button } from '@optools/components/ui/button';
import { Card } from '@optools/components/ui/card';
import { cn } from '@optools/components/ui/utils';
import { RETURN_PIPELINE } from '@optools/services/operator-gateway/qa-tools/mutations';
import { PIPELINE_MONITORS } from '@optools/services/operator-gateway/qa-tools/queries';
import { PipelineMonitorsQuery } from '@optools/services/operator-gateway/types/generated/graphql';
import { useTeamsQuery } from '@optools/store/statsApiSlice';
import { MinusCircle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export interface ProdPipelineMonitorCardProps {
  monitor: PipelineMonitorsQuery['pipelineMonitors'][number];
}

export function ProdPipelineMonitorCard({ monitor }: ProdPipelineMonitorCardProps) {
  const { eventPipeline, pipelineConfig } = monitor;

  const [open, setIsOpen] = useState(false);
  const { data } = useTeamsQuery();
  const [returnPipeline, { loading }] = useMutation(RETURN_PIPELINE, {
    onCompleted: ({ returnPipeline }) => {
      if (!returnPipeline) {
        toast.error('Return Pipeline Error', {
          description: `Failed to return pipeline for team: ${teamId} to production`,
          duration: Infinity,
        });
        return;
      } else {
        toast.success('Return Pipeline', {
          description: `Pipeline for team: ${teamId} returned to production`,
        });
        setIsOpen(false);
      }
    },
    update: (cache, { data }) => {
      // Choose to update the cache here vs. refresh as the backend can take some time to
      // update the pipelineMonitors.
      if (!data?.returnPipeline) return;

      const query = cache.readQuery<PipelineMonitorsQuery>({
        query: PIPELINE_MONITORS,
      });

      if (!query) return;

      const index = query.pipelineMonitors.findIndex(
        (monitor) => monitor.pipelineConfig?.teamId === teamId,
      );

      if (index === -1) return;

      const pipelineMonitors = [...query.pipelineMonitors];
      pipelineMonitors.splice(index, 1);

      cache.writeQuery<PipelineMonitorsQuery>({
        data: { pipelineMonitors },
        query: PIPELINE_MONITORS,
      });
    },
  });

  if (!pipelineConfig) return null;

  const { fronthaulDecoderServiceLabel, id, teamId } = pipelineConfig;

  const selectedTeam = data?.find((team) => team.id === teamId);

  const isAvailable = !eventPipeline;

  const handleReturnPipeline = async () => {
    if (!teamId) {
      return;
    }

    await returnPipeline({ variables: { teamId } });
  };

  return (
    <Card className="h-full p-4" data-testid={`team-${teamId}-borrowed-pipeline`}>
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="mb-1 flex items-center gap-2">
            <TeamSpot showAbbreviation={false} team={selectedTeam} />
            <h2 className="font-mono text-lg font-medium">{selectedTeam?.abbreviation}</h2>
            <Badge className="self-center" variant="outline">
              {id}
            </Badge>
          </div>
          <p className="text-xs text-primary/80">{fronthaulDecoderServiceLabel}</p>
        </div>
        <div className="flex flex-col gap-3">
          <AlertDialog open={open} onOpenChange={setIsOpen}>
            <AlertDialogTrigger asChild>
              <Button
                aria-label="return pipeline to production"
                className="self-end"
                size="icon-sm"
                variant="destructive">
                <MinusCircle />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Return Pipeline to Production</AlertDialogTitle>
                <AlertDialogDescription>{`Are you sure you wish to return pipeline for ${selectedTeam?.abbreviation} to production?`}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <Button variant="secondary" onClick={() => setIsOpen(false)}>
                  Cancel
                </Button>
                <Button
                  disabled={loading}
                  loading={loading}
                  variant="destructive"
                  onClick={handleReturnPipeline}>
                  Return
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Badge
            className={cn(
              isAvailable ? 'border-green-500 text-green-500' : 'border-yellow-500 text-yellow-500',
            )}
            variant="outline">
            <span className="mr-0.5 size-2 rounded-full bg-current" />
            {isAvailable ? 'Available' : 'In Use'}
          </Badge>
        </div>
      </div>

      {!isAvailable && (
        <Card className="mt-2 flex items-center justify-between rounded-md p-2">
          <h3 className="text-sm">Pipeline Status</h3>
          <PipelineStatusBadge status={eventPipeline?.status} />
        </Card>
      )}
    </Card>
  );
}
